import { useState } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";

import { Modal } from "../../components/Modal/Modal";
import { Input } from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import styles from "./RotateConfirmModal.module.css";

interface Props {
  open: boolean;
  projectName: string;
  /** Last 4 chars of the current key, surfaced inline in the body copy. */
  currentKeyLast4?: string;
  onClose: () => void;
  onConfirmed: () => void | Promise<void>;
}

/**
 * Type-to-confirm modal for rotating a project's API key. Mirrors mockup
 * L477-L505 — warn icon header, irreversible danger alert, a chip showing
 * the project name to type, and a green "✓ Project name matches" helper.
 */
export function RotateConfirmModal({
  open,
  projectName,
  currentKeyLast4,
  onClose,
  onConfirmed,
}: Props) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const matches = typed.trim() === projectName && projectName.length > 0;

  function reset() {
    setTyped("");
  }

  async function confirm() {
    if (!matches || busy) return;
    setBusy(true);
    try {
      await onConfirmed();
      reset();
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    reset();
    onClose();
  }

  const keyHint = currentKeyLast4 ? `…${currentKeyLast4}` : "…";

  const title = projectName
    ? `Rotate ${projectName} API key?`
    : "Rotate API key?";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void confirm()}
            disabled={!matches || busy}
            loading={busy}
            icon={<RotateCw size={14} aria-hidden="true" />}
          >
            Rotate key now
          </Button>
        </>
      }
    >
      <div className={styles.warnIcon} aria-hidden="true">
        <AlertTriangle size={18} />
      </div>

      <p className={styles.lead}>
        The current key (<code className={styles.inlineCode}>{keyHint}</code>)
        is revoked immediately. Your game server loses access until it is
        updated. Players are unaffected.
      </p>

      <div className={styles.alertWrap}>
        <Alert variant="danger" title="This is irreversible.">
          A new key will be issued and the old one stops working immediately.
        </Alert>
      </div>

      <div className={styles.confirmLabelRow}>
        <span className={styles.confirmLabelText}>
          Type{" "}
          <span className={styles.confirmQuote}>{projectName}</span>{" "}
          to confirm
        </span>
      </div>

      <Input
        aria-label={`Type ${projectName} to confirm`}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoFocus
        className={`${styles.confirmInput} ${matches ? styles.confirmInputOk : ""}`}
        hint={matches ? "✓ Project name matches" : undefined}
        helperVariant={matches ? "ok" : "default"}
      />
    </Modal>
  );
}
